"use client";

import Image, { StaticImageData } from "next/image";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { isSameOriginSvgPath, sanitizeSvg } from "@/lib/security/sanitize-svg";

interface IconProps {
	src: StaticImageData | string;
	alt?: string;
	size?: number;
	className?: string;
	/**
	 * When true, omit `content-icon-svg` so globals do not force every path to mint/blue.
	 * Use with a scoped wrapper class + `globals.css` for multi-color SVGs (e.g. profile bill.svg).
	 */
	disableContentIconTheming?: boolean;
}

/**
 * Universal Icon component that ensures consistent sizing for both SVG and PNG icons.
 * For SVG files, renders inline SVG to allow direct path styling (preserving green, converting black to blue).
 * For other formats, uses Next.js Image component.
 */
export default function Icon({
	src,
	alt = "icon",
	size = 30,
	className,
	disableContentIconTheming = false,
}: IconProps) {
	// Handle both string paths and StaticImageData objects
	const srcPath = typeof src === "string" ? src : (src as StaticImageData).src;
	const isSvg = srcPath.endsWith(".svg");
	const [svgContent, setSvgContent] = useState<string | null>(null);
	
	// Function to process SVG content and mark green elements
	const processSvgContent = (text: string) => {
		// Parse SVG and mark green elements with data attribute
		// Green color is #3fdfcf or #3FE0D0 (Techxeed secondary)
		const parser = new DOMParser();
		const svgDoc = parser.parseFromString(text, "image/svg+xml");
		const svgElement = svgDoc.documentElement;
		
		// Find style tag and extract green classes
		const styleTag = svgElement.querySelector("style");
		const greenClasses = new Set<string>();
		
		if (styleTag) {
			const styleText = styleTag.textContent || "";
			// Match class definitions with green fill (#3fdfcf or #3FE0D0)
			const classRegex = /\.([\w-]+)\s*\{[^}]*fill:\s*(#3fdfcf|#3FE0D0)/gi;
			let match;
			while ((match = classRegex.exec(styleText)) !== null) {
				greenClasses.add(match[1]);
			}
		}
		
		// Mark elements with green classes or green fill attributes
		const allElements = svgElement.querySelectorAll("*");
		allElements.forEach((el) => {
			const fill = el.getAttribute("fill");
			// Check if fill attribute is green
			if (fill === "#3fdfcf" || fill === "#3FE0D0") {
				el.setAttribute("data-preserve-green", "true");
			}
			
			// Check if element has a green class
			const classList = Array.from(el.classList);
			classList.forEach((cls) => {
				if (greenClasses.has(cls)) {
					el.setAttribute("data-preserve-green", "true");
				}
			});
		});
		
		// Convert back to string and sanitize to prevent XSS
		const serializer = new XMLSerializer();
		const modifiedSvg = serializer.serializeToString(svgElement);
		setSvgContent(sanitizeSvg(modifiedSvg));
	};

	useEffect(() => {
		if (isSvg) {
			// SECURITY: Only fetch and inject SVG from same-origin paths (no external URLs)
			if (!isSameOriginSvgPath(srcPath)) {
				setSvgContent(null);
				return;
			}
			// Fetch SVG content to render inline
			// Handle different path formats from Next.js
			let fetchPath = srcPath;
			
			// If it's a Next.js processed path (starts with /_next), use it as-is
			// If it starts with /public, remove the prefix
			// Otherwise, try to construct the path
			if (fetchPath.startsWith("/public")) {
				fetchPath = fetchPath.replace("/public", "");
			} else if (!fetchPath.startsWith("/") && !fetchPath.startsWith("http")) {
				// If it's a relative path, try to make it absolute
				fetchPath = `/${fetchPath}`;
			}
			
			// Also try the original path if it's a StaticImageData object
			if (typeof src !== "string" && (src as StaticImageData).src) {
				const originalSrc = (src as StaticImageData).src;
				// Try original path first, then fallback to processed path
				const pathsToTry = [
					originalSrc,
					fetchPath,
					srcPath.startsWith("/public") ? srcPath.replace("/public", "") : srcPath,
				];
				
				// Try each path until one works
				const tryFetch = (path: string): Promise<string> => {
					return fetch(path)
						.then((res) => {
							if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
							return res.text();
						});
				};
				
				Promise.any(pathsToTry.map(tryFetch))
					.then((text) => {
						// Parse and process SVG
						processSvgContent(text);
					})
					.catch((err) => {
						console.warn("Failed to load SVG inline, falling back to Image component:", err);
						setSvgContent(null);
					});
				return;
			}
			
			fetch(fetchPath)
				.then((res) => {
					if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
					return res.text();
				})
				.then((text) => {
					processSvgContent(text);
				})
				.catch((err) => {
					console.warn("Failed to load SVG inline, falling back to Image component:", err);
					setSvgContent(null);
				});
		}
	}, [isSvg, srcPath]);

	if (isSvg && svgContent) {
		// Render inline SVG for direct path styling
		return (
			<div
				className={cn(
					"flex items-center justify-center shrink-0",
					!disableContentIconTheming && "content-icon-svg",
					className,
				)}
				style={{ width: size, height: size, minWidth: size, minHeight: size }}
				dangerouslySetInnerHTML={{ __html: svgContent }}
			/>
		);
	}

	// Fallback to Image component for non-SVG or if SVG fetch fails
	return (
		<div
			className={cn(
				"flex items-center justify-center shrink-0",
				isSvg && !disableContentIconTheming && "content-icon-blue",
				className,
			)}
			style={{ width: size, height: size, minWidth: size, minHeight: size }}
		>
			<Image
				draggable="false"
				src={src}
				width={size}
				height={size}
				alt={alt}
				className="size-full object-contain"
				unoptimized={isSvg}
			/>
		</div>
	);
}

